import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { GALLERY, GALLERY_CATEGORIES } from "../../data/content";
import { Overline } from "./Reveal";

export const GalleryPage = () => {
  const [filter, setFilter] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  const items = filter === "All" ? GALLERY : GALLERY.filter((g) => g.category === filter);

  return (
    <div className="min-h-screen bg-[#0c2c20] pt-32">
      <section className="mx-auto max-w-[1400px] px-6 pb-16 md:px-12">
        <Overline>Portfolio Gallery</Overline>
        <h1 className="mt-6 max-w-4xl font-display text-4xl font-bold leading-[1.02] text-white sm:text-5xl lg:text-7xl">
          A career, <span className="font-serif-e italic text-[#c9a24e]">in the field.</span>
        </h1>
        <p className="mt-6 max-w-xl text-[15px] leading-relaxed text-[#a1a1aa]">
          From sorghum biofuel projects and boardrooms to development sites, marinas and
          international events — moments from across the industries I&apos;ve led and where I&apos;m headed.
        </p>

        <div className="mt-12 flex flex-wrap gap-3">
          {GALLERY_CATEGORIES.map((cat) => (
            <button
              key={cat}
              data-testid={`gallery-filter-${cat.replace(/[^a-z]/gi, "-").toLowerCase()}`}
              onClick={() => setFilter(cat)}
              className={`border px-5 py-2 text-[12px] uppercase tracking-[0.18em] transition-colors duration-300 ${
                filter === cat
                  ? "border-[#c9a24e] bg-[#c9a24e] text-black"
                  : "border-white/15 text-[#a1a1aa] hover:border-[#c9a24e] hover:text-white"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 pb-32 md:px-12">
        <motion.div layout className="columns-1 gap-5 sm:columns-2 lg:columns-3 [&>*]:mb-5">
          <AnimatePresence>
            {items.map((item, i) => (
              <motion.button
                layout
                key={item.url}
                data-testid={`gallery-item-${i}`}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, delay: (i % 6) * 0.05 }}
                onClick={() => setLightbox(item)}
                className="group relative block w-full overflow-hidden break-inside-avoid border border-white/10"
              >
                <img
                  src={item.url}
                  alt={item.title}
                  loading="lazy"
                  className="w-full transition-transform duration-700 ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 flex items-end bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                  <div className="p-6 text-left">
                    <span className="text-[10px] uppercase tracking-[0.25em] text-[#c9a24e]">
                      {item.category}
                    </span>
                    <p className="mt-1 font-display text-xl text-white">{item.title}</p>
                  </div>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
      </section>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-6 backdrop-blur-xl"
          >
            <button
              data-testid="gallery-lightbox-close"
              onClick={() => setLightbox(null)}
              className="absolute right-6 top-6 flex h-11 w-11 items-center justify-center border border-white/20 text-white transition-colors hover:bg-[#c9a24e] hover:text-black"
            >
              <X size={20} />
            </button>
            <motion.div
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.94, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[85vh] max-w-5xl"
            >
              <img src={lightbox.url} alt={lightbox.title} className="max-h-[78vh] w-auto border border-white/10" />
              <div className="mt-4 flex items-center justify-between">
                <p className="font-display text-lg text-white">{lightbox.title}</p>
                <span className="text-[11px] uppercase tracking-[0.25em] text-[#c9a24e]">
                  {lightbox.category}
                </span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
